import React from 'react';
import { Link } from '@mui/material';
import { resolveScopeLabel } from './scopeUtils';

/** Parse inline **bold** in a line and return React nodes */
export function renderLineWithBold(line) {
    const parts = line.split('**');
    if (parts.length === 1) return line;
    return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

const LESSON_BLOCK_REGEX = /\{\{#lesson\s+"([^"]+)"\}\}(.*?)\{\{\/lesson\}\}/g;

/** Split a line into segments: plain text and {{#lesson "id"}}label{{/lesson}} blocks */
function splitLessonBlocks(line) {
    const segments = [];
    let lastIndex = 0;
    let match;
    LESSON_BLOCK_REGEX.lastIndex = 0;
    while ((match = LESSON_BLOCK_REGEX.exec(line)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: line.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'lesson', id: match[1], label: match[2].trim() });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) {
        segments.push({ type: 'text', value: line.slice(lastIndex) });
    }
    return segments.length ? segments : [{ type: 'text', value: line }];
}

/** Render a text segment with {{type:id}} scope chips and **bold**. */
function renderTextWithScopesAndBold(text, classes, students, baseKey) {
    const segments = text.split(/(\{\{\w+:[^}]+\}\})/g);
    return segments.map((seg, i) => {
        const m = seg.match(/^\{\{(\w+):([^}]+)\}\}$/);
        if (m) {
            const [, type, id] = m;
            const label = resolveScopeLabel(type, id, classes, students);
            return (
                <span key={`${baseKey}-${i}`} className="ai-chat-inline-scope">
                    {label}
                </span>
            );
        }
        return <span key={`${baseKey}-${i}`}>{renderLineWithBold(seg)}</span>;
    });
}

/** Parse a message line: {{#lesson "id"}}name{{/lesson}} → links (click opens lesson preview), {{type:id}} → scope chips, **bold**. */
export function renderMessageLine(line, classes, students, lineKey, onLessonClick) {
    const segments = splitLessonBlocks(line);
    return segments.map((seg, i) => {
        const key = `${lineKey}-${i}`;
        if (seg.type === 'lesson') {
            return (
                <Link
                    key={key}
                    component="button"
                    variant="body2"
                    onClick={() => onLessonClick?.(seg.id)}
                    sx={{
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        color: 'primary.main',
                        '&:hover': { color: 'primary.dark' },
                        verticalAlign: 'baseline',
                        p: 0,
                        m: 0,
                        border: 0,
                        background: 'none',
                    }}
                >
                    {seg.label}
                </Link>
            );
        }
        return <span key={key}>{renderTextWithScopesAndBold(seg.value, classes, students, key)}</span>;
    });
}
